import React, { useRef, useState, useCallback } from 'react';
import { Box, Button, IconButton, Typography, keyframes } from '@mui/material';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import toast, { Toast as ToastType } from 'react-hot-toast';
import { NotificationLevels } from '~/context/Notifications';

type NotificationProps = {
  level: NotificationLevels;
  message: React.ReactNode;
  t: ToastType;
  action?: () => void;
  actionLabel?: string;
  onClose?: () => void;
};

const levelConfig = {
  success: {
    title: 'Success',
    Icon: CheckCircleIcon,
    paletteKey: 'success' as const,
  },
  danger: {
    title: 'Error',
    Icon: ErrorIcon,
    paletteKey: 'error' as const,
  },
  warning: {
    title: 'Warning',
    Icon: WarningIcon,
    paletteKey: 'warning' as const,
  },
  info: {
    title: 'Info',
    Icon: InfoIcon,
    paletteKey: 'info' as const,
  },
  primary: {
    title: 'Notice',
    Icon: NotificationsIcon,
    paletteKey: 'dianomiGreen' as const,
  },
};

const countdownAnimation = keyframes`
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
`;

function Notification({ level, message, t, action, actionLabel, onClose }: NotificationProps) {
  const theme = useTheme();
  const config = levelConfig[level];
  const mainColor = theme.palette[config.paletteKey].main;
  const borderColor = lighten(mainColor, 0.4);
  const lightColor = lighten(mainColor, 0.8);
  const { Icon } = config;

  const hasDuration = typeof t.duration === 'number' && t.duration !== Infinity && t.duration > 0;
  const durationMs = hasDuration ? t.duration : 0;

  const [isHovering, setIsHovering] = useState(false);
  const startTimeRef = useRef(Date.now());

  const checkIfExpired = useCallback(() => {
    if (!hasDuration || !durationMs) return false;
    const elapsed = Date.now() - startTimeRef.current;
    return elapsed >= durationMs;
  }, [hasDuration, durationMs]);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    // if countdown elapsed while hovering, dismiss
    if (checkIfExpired()) {
      toast.dismiss(t.id);
    }
  }, [checkIfExpired, t.id]);

  const handleAnimationEnd = useCallback(() => {
    if (!isHovering) {
      toast.dismiss(t.id);
    }
  }, [isHovering, t.id]);

  return (
    <Box
      role="alert"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '4px',
        backgroundColor: alpha(lightColor, 0.65),
        backdropFilter: 'blur(8px) saturate(1.15)',
        WebkitBackdropFilter: 'blur(8px) saturate(1.15)',
        border: `2px solid ${alpha(borderColor, 0.45)}`,
        boxShadow: `
          0 4px 12px rgba(0, 0, 0, 0.12),
          inset 0 1px 0 rgba(255, 255, 255, 0.35)
        `,
        opacity: t.visible ? 1 : 0,
        transform: t.visible ? 'translateY(0) scale(1)' : 'translateY(-6px) scale(0.98)',
        transition: 'opacity 180ms ease, transform 180ms ease',
        willChange: 'opacity, transform',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width: 'fit-content',
          maxWidth: { xs: 300, sm: 500 },
          padding: '8px 10px',
          paddingBottom: hasDuration ? '12px' : '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: 1,
            '& > :nth-child(3)': {
              alignSelf: 'center',
            },
          }}
        >
          <Icon sx={{ color: mainColor, fontSize: { xs: 14, sm: 16, lg: 18 } }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'start',
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.7rem', lg: '0.75rem', xl: '0.8rem' },
                color: 'primary.main',
                wordBreak: 'break-word',
                fontWeight: 600,
              }}
              component="div"
            >
              {message}
            </Typography>
            {action && actionLabel && (
              <Button
                size="small"
                onClick={action}
                sx={{
                  display: 'flex',
                  justifyContent: 'start',
                  textTransform: 'none',
                  maxWidth: 'fit-content',
                  color: 'blue',
                  fontSize: { xs: '0.7rem', lg: '0.75rem', xl: '0.75rem' },
                  fontWeight: 700,
                  paddingLeft: 0,
                  padding: 0,
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {actionLabel}
              </Button>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              if (onClose) {
                onClose();
                return;
              }
              toast.dismiss(t.id);
            }}
            sx={{
              padding: { xs: 0.5, lg: 0.8 },
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'secondary.main',
                borderRadius: '50%',
              },
            }}
            aria-label="Close notification"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
      {hasDuration && (
        <Box
          onAnimationEnd={handleAnimationEnd}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '4px',
            backgroundColor: mainColor,
            borderRadius: '0 0 4px 4px',
            zIndex: 2,
            transformOrigin: 'left',
            animation: `${countdownAnimation} ${durationMs}ms linear forwards`,
          }}
        />
      )}
    </Box>
  );
}

export default Notification;
